import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import qs from "querystring";
import styled from "styled-components";

import DateRangeSelector from "./DateRangeSelector";
import { useGetStatsHistoryQuery } from "./state/api";
import {
    readDateRangeFromSearch,
    numDaysToToday,
    durationStringToDays,
    isoToYMD,
} from "./utils";

export default function TrendsHeader({ workspaceNumber, effectiveEndIso }) {
    const history  = useHistory();
    const location = useLocation();

    const { startIso, endIso } = readDateRangeFromSearch(location.search);
    const numDays = numDaysToToday({ startIso });

    const query = qs.parse(location.search.replace(/^\?/, ""));
    const urlRange    = typeof query.range === "string" ? query.range : undefined;
    const hasValidRange = urlRange && durationStringToDays(urlRange);
    const hasStartEnd   = typeof query.start === "string" && typeof query.end === "string";

    const selectorMode = hasValidRange ? urlRange : hasStartEnd ? "custom" : "1m";
    const isNonDefault = selectorMode !== "1m";

    const { isFetching, isLoading, isError, error, refetch } = useGetStatsHistoryQuery(
        { workspaceNumber, size: numDays },
        { skip: workspaceNumber == null }
    );

    const writeQuery = ({ preset, startIso, endIso }) => {
        const current = qs.parse(location.search.replace(/^\?/, ""));
        if (preset && preset !== "custom") {
            current.range = preset;
            delete current.start;
            delete current.end;
        } else {
            delete current.range;
            current.start = isoToYMD(startIso);
            current.end   = isoToYMD(endIso);
        }
        const nextSearch = "?" + qs.stringify(current);
        if (nextSearch !== location.search) history.replace({ search: nextSearch });
    };

    const reset = () => {
        const current = qs.parse(location.search.replace(/^\?/, ""));
        delete current.range;
        delete current.start;
        delete current.end;
        history.replace({ search: "?" + qs.stringify(current) });
    };

    return (
        <Bar>
            <DateRangeSelector
                startDate={startIso}
                endDate={effectiveEndIso || endIso}
                preset={selectorMode}
                onChange={writeQuery}
            />
            {isNonDefault && <ResetBtn onClick={reset}>Reset</ResetBtn>}
            <Right>
                {(isLoading || isFetching) && <StatusBadge>Loading…</StatusBadge>}
                {isError && (
                    <ErrorBadge onClick={() => refetch()} title={String(error?.status || error?.error || "Error")}>
                        Error — click to retry
                    </ErrorBadge>
                )}
            </Right>
        </Bar>
    );
}

const Bar = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    background: #ffffff;
    padding: 12px 20px;
    margin-bottom: 16px;
    @media (max-width: 600px) {
        border-radius: 0;
        border-left: none;
        border-right: none;
        padding: 12px 14px;
    }
`;

const Right = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-left: auto;
`;

const ResetBtn = styled.button`
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    border-radius: 6px;
    border: 1px solid #fdba74;
    background: #fff7ed;
    color: #c2410c;
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 120ms;
    &:hover { background: #fed7aa; border-color: #fb923c; }
`;

const StatusBadge = styled.span`
    font-size: 0.7rem;
    font-weight: 500;
    padding: 2px 8px;
    border-radius: 99px;
    background: #f1f5f9;
    color: #64748b;
`;

const ErrorBadge = styled.button`
    font-size: 0.7rem;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 99px;
    background: #fef2f2;
    color: #dc2626;
    border: none;
    cursor: pointer;
    &:hover { background: #fee2e2; }
`;
